/**
 * UI string catalogs for Modyra controls — pure data plus message
 * functions, shared by every framework adapter. Tokens/providers that
 * inject these live in the framework packages.
 */
/** Default English strings. Replace individual keys by spreading over this. */
/**
 * The names a rejection message lists, however they arrive.
 *
 * The parameter is declared as a list and every caller in this package passes one, but a message is
 * a string a host may also call directly — from a log line, a test, a translation tool checking that
 * a sentence carries what it was given. A message that raises is worse than one that reads oddly:
 * the control ends up with no text at all, in that language only.
 */
function namesInMessage(names) {
    return Array.isArray(names) ? names.join(", ") : String(names);
}
export const MDY_I18N_MESSAGES_DEFAULT = Object.freeze({
    searchPlaceholder: "Search\u2026",
    selectPlaceholder: "Select\u2026",
    entryUnreadable: "That could not be read. Leave it and correct it, or clear the field.",
    noResults: "No results",
    colorPresetsHeader: "Presets",
    selectColorPrefix: "Select color",
    colorCustomEntry: "All colours…",
    colorCustomValue: "Custom, {value}",
    colorHexLabel: "Color hex value",
    timepickerOpenLabel: "Open time picker",
    timepickerCancel: "Cancel",
    timepickerConfirm: "OK",
    timepickerHourLabel: "Hour",
    timepickerMinuteLabel: "Minute",
    timepickerSwitchToDial: "Switch to dial view",
    timepickerSwitchToInput: "Switch to text input",
    datepickerToggleLabel: "Toggle calendar",
    datepickerCancel: "Cancel",
    datepickerConfirm: "OK",
    datepickerSelectFallback: "Select date",
    datepickerChooseDate: "Choose date",
    datepickerPreviousMonth: "Previous month",
    datepickerNextMonth: "Next month",
    datepickerChangeView: (current) => `Change view, currently ${current}`,
    daterangeChooseRange: "Choose date range",
    daterangeSelectFallback: "Select range",
    daterangeStartLabel: "Start date",
    daterangeEndLabel: "End date",
    daterangePickStartHint: "Click to set the start date",
    daterangePickEndHint: "Click to set the end date",
    loading: "Loading\u2026",
    increase: "Increase",
    decrease: "Decrease",
    searchOptionsLabel: "Search options",
    chipRemoveLabel: "Remove",
    chipMoveEarlierLabel: "Move earlier",
    chipMoveLaterLabel: "Move later",
    quantitySettled: "{value}, {count}",
    quantityAtMinimum: "{value}, {count}, minimum",
    selectionAdded: "{value} added, {count} selected",
    selectionRemoved: "{value} removed, {count} selected",
    selectionEmpty: "Nothing selected",
    selectionRemovedLast: "{value} removed, nothing selected",
    selectionAddedMany: "{moved} added, {count} selected",
    selectionRemovedMany: "{moved} removed, {count} selected",
    selectionRemovedManyLast: "{moved} removed, nothing selected",
    selectionMoved: "{value}, moved to position {position} of {count}",
    keyGuideOpen: "{keys} opens it",
    keyGuideMove: "{keys} moves through it",
    keyGuideStep: "{keys} changes the value",
    keyGuideToggle: "{keys} switches it",
    keyGuideCommit: "{keys} confirms",
    keyGuideCancel: "{keys} closes it",
    keyGuideRemove: "{keys} takes one off",
    keyGuideGrab: "{keys} picks one up to move it",
    keyGuideOr: " or ",
    keyGuideJoin: ", ",
    keyGuideSpace: "Space",
    selectionGrabbed: "{value} grabbed, {position} of {count}. Use the arrows to move it, Enter to drop it, Escape to put it back",
    selectionDropped: "{value} dropped at position {position} of {count}",
    selectionReturned: "{value} returned to position {position} of {count}",
    selectionCount: "{count} selected",
    clearSelection: "Clear selection",
    chipsHiddenShort: "+{count}",
    chipsHidden: "{count} more not shown",
    wayBackLabel: "Undo",
    wayBackRemoved: "{value} removed",
    wayBackMoved: "{value} moved",
    wayBackCleared: "{count} items cleared",
    chipDecrementLabel: "One fewer",
    chipIncrementLabel: "One more",
    fileSelect: "Select file",
    fileSelectMultiple: "Select files",
    fileNoneSelected: "No file selected",
    fileClearSelection: "Clear selection",
    fileRejected: (names) => `Not accepted: ${namesInMessage(names)}`,
    overlayOpened: "Popup opened",
    overlayClosed: "Popup closed",
    selectCreateOption: (query) => `Create "${query}"`,
    wizardNext: "Next",
    wizardPrevious: "Back",
    wizardFinish: "Finish",
    wizardStepStatus: (current, total) => `Step ${current} of ${total}`,
});
export const MDY_I18N_MESSAGES_IT = Object.freeze({
    searchPlaceholder: "Cerca…",
    selectPlaceholder: "Seleziona…",
    entryUnreadable: "Non è stato possibile leggerlo. Correggilo, oppure svuota il campo.",
    noResults: "Nessun risultato",
    colorPresetsHeader: "Preset",
    selectColorPrefix: "Seleziona colore",
    colorCustomEntry: "Tutti i colori…",
    colorCustomValue: "Personalizzato, {value}",
    colorHexLabel: "Valore esadecimale del colore",
    timepickerOpenLabel: "Apri selettore orario",
    timepickerCancel: "Annulla",
    timepickerConfirm: "OK",
    timepickerHourLabel: "Ora",
    timepickerMinuteLabel: "Minuti",
    timepickerSwitchToDial: "Passa alla vista quadrante",
    timepickerSwitchToInput: "Passa all'inserimento testuale",
    datepickerToggleLabel: "Apri/chiudi calendario",
    datepickerCancel: "Annulla",
    datepickerConfirm: "OK",
    datepickerSelectFallback: "Seleziona data",
    datepickerChooseDate: "Scegli la data",
    datepickerPreviousMonth: "Mese precedente",
    datepickerNextMonth: "Mese successivo",
    datepickerChangeView: (current) => `Cambia vista, attuale ${current}`,
    daterangeChooseRange: "Scegli l'intervallo di date",
    daterangeSelectFallback: "Seleziona intervallo",
    daterangeStartLabel: "Data di inizio",
    daterangeEndLabel: "Data di fine",
    daterangePickStartHint: "Clicca per impostare la data di inizio",
    daterangePickEndHint: "Clicca per impostare la data di fine",
    loading: "Caricamento…",
    increase: "Aumenta",
    decrease: "Diminuisci",
    searchOptionsLabel: "Cerca tra le opzioni",
    chipRemoveLabel: "Rimuovi",
    chipMoveEarlierLabel: "Sposta prima",
    chipMoveLaterLabel: "Sposta dopo",
    quantitySettled: "{value}, {count}",
    quantityAtMinimum: "{value}, {count}, minimo",
    selectionAdded: "{value} aggiunto, {count} selezionati",
    selectionRemoved: "{value} rimosso, {count} selezionati",
    selectionEmpty: "Nessuna selezione",
    selectionRemovedLast: "{value} rimosso, nessuna selezione",
    selectionAddedMany: "{moved} aggiunti, {count} selezionati",
    selectionRemovedMany: "{moved} rimossi, {count} selezionati",
    selectionRemovedManyLast: "{moved} rimossi, nessuna selezione",
    selectionMoved: "{value}, spostato in posizione {position} di {count}",
    keyGuideOpen: "{keys} lo apre",
    keyGuideMove: "{keys} lo percorre",
    keyGuideStep: "{keys} cambia il valore",
    keyGuideToggle: "{keys} lo commuta",
    keyGuideCommit: "{keys} conferma",
    keyGuideCancel: "{keys} lo chiude",
    keyGuideRemove: "{keys} ne toglie uno",
    keyGuideGrab: "{keys} ne prende uno per spostarlo",
    keyGuideOr: " o ",
    keyGuideJoin: ", ",
    keyGuideSpace: "Spazio",
    selectionGrabbed: "{value} preso, {position} di {count}. Usa le frecce per spostarlo, Invio per posarlo, Esc per rimetterlo dov'era",
    selectionDropped: "{value} posato in posizione {position} di {count}",
    selectionReturned: "{value} rimesso in posizione {position} di {count}",
    selectionCount: "{count} selezionati",
    clearSelection: "Svuota la selezione",
    chipsHiddenShort: "+{count}",
    chipsHidden: "altri {count} non mostrati",
    wayBackLabel: "Annulla",
    wayBackRemoved: "{value} rimosso",
    wayBackMoved: "{value} spostato",
    wayBackCleared: "{count} elementi rimossi",
    chipDecrementLabel: "Uno in meno",
    chipIncrementLabel: "Uno in più",
    fileSelect: "Seleziona file",
    fileSelectMultiple: "Seleziona file",
    fileNoneSelected: "Nessun file selezionato",
    fileClearSelection: "Rimuovi selezione",
    fileRejected: (names) => `Non accettati: ${namesInMessage(names)}`,
    overlayOpened: "Popup aperto",
    overlayClosed: "Popup chiuso",
    selectCreateOption: (query) => `Crea "${query}"`,
    wizardNext: "Avanti",
    wizardPrevious: "Indietro",
    wizardFinish: "Fine",
    wizardStepStatus: (current, total) => `Passo ${current} di ${total}`,
});
export const MDY_I18N_MESSAGES_DE = Object.freeze({
    searchPlaceholder: "Suchen…",
    selectPlaceholder: "Auswählen…",
    entryUnreadable: "Das konnte nicht gelesen werden. Korrigieren Sie es, oder leeren Sie das Feld.",
    noResults: "Keine Ergebnisse",
    colorPresetsHeader: "Voreinstellungen",
    selectColorPrefix: "Farbe auswählen",
    colorCustomEntry: "Alle Farben…",
    colorCustomValue: "Benutzerdefiniert, {value}",
    colorHexLabel: "Hex-Farbwert",
    timepickerOpenLabel: "Zeitauswahl öffnen",
    timepickerCancel: "Abbrechen",
    timepickerConfirm: "OK",
    timepickerHourLabel: "Stunde",
    timepickerMinuteLabel: "Minute",
    timepickerSwitchToDial: "Zur Ziffernblattansicht wechseln",
    timepickerSwitchToInput: "Zur Texteingabe wechseln",
    datepickerToggleLabel: "Kalender ein-/ausblenden",
    datepickerCancel: "Abbrechen",
    datepickerConfirm: "OK",
    datepickerSelectFallback: "Datum auswählen",
    datepickerChooseDate: "Datum wählen",
    datepickerPreviousMonth: "Vorheriger Monat",
    datepickerNextMonth: "Nächster Monat",
    datepickerChangeView: (current) => `Ansicht wechseln, aktuell ${current}`,
    daterangeChooseRange: "Zeitraum wählen",
    daterangeSelectFallback: "Zeitraum auswählen",
    daterangeStartLabel: "Startdatum",
    daterangeEndLabel: "Enddatum",
    daterangePickStartHint: "Klicken, um das Startdatum zu setzen",
    daterangePickEndHint: "Klicken, um das Enddatum zu setzen",
    loading: "Wird geladen…",
    increase: "Erhöhen",
    decrease: "Verringern",
    searchOptionsLabel: "Optionen durchsuchen",
    chipRemoveLabel: "Entfernen",
    chipMoveEarlierLabel: "Nach vorne",
    chipMoveLaterLabel: "Nach hinten",
    quantitySettled: "{value}, {count}",
    quantityAtMinimum: "{value}, {count}, Minimum",
    selectionAdded: "{value} hinzugefügt, {count} ausgewählt",
    selectionRemoved: "{value} entfernt, {count} ausgewählt",
    selectionEmpty: "Nichts ausgewählt",
    selectionRemovedLast: "{value} entfernt, nichts ausgewählt",
    selectionAddedMany: "{moved} hinzugefügt, {count} ausgewählt",
    selectionRemovedMany: "{moved} entfernt, {count} ausgewählt",
    selectionRemovedManyLast: "{moved} entfernt, nichts ausgewählt",
    selectionMoved: "{value}, verschoben auf Position {position} von {count}",
    keyGuideOpen: "{keys} öffnet es",
    keyGuideMove: "{keys} bewegt sich darin",
    keyGuideStep: "{keys} ändert den Wert",
    keyGuideToggle: "{keys} schaltet um",
    keyGuideCommit: "{keys} bestätigt",
    keyGuideCancel: "{keys} schließt es",
    keyGuideRemove: "{keys} entfernt eines",
    keyGuideGrab: "{keys} nimmt eines auf, um es zu bewegen",
    keyGuideOr: " oder ",
    keyGuideJoin: ", ",
    keyGuideSpace: "Leertaste",
    selectionGrabbed: "{value} aufgenommen, {position} von {count}. Mit den Pfeiltasten bewegen, Eingabe zum Ablegen, Escape zum Zurücklegen",
    selectionDropped: "{value} abgelegt auf Position {position} von {count}",
    selectionReturned: "{value} zurückgelegt auf Position {position} von {count}",
    selectionCount: "{count} ausgewählt",
    clearSelection: "Auswahl leeren",
    chipsHiddenShort: "+{count}",
    chipsHidden: "{count} weitere nicht sichtbar",
    wayBackLabel: "Rückgängig",
    wayBackRemoved: "{value} entfernt",
    wayBackMoved: "{value} verschoben",
    wayBackCleared: "{count} Einträge entfernt",
    chipDecrementLabel: "Einer weniger",
    chipIncrementLabel: "Einer mehr",
    fileSelect: "Datei auswählen",
    fileSelectMultiple: "Dateien auswählen",
    fileNoneSelected: "Keine Datei ausgewählt",
    fileClearSelection: "Auswahl entfernen",
    fileRejected: (names) => `Nicht akzeptiert: ${namesInMessage(names)}`,
    overlayOpened: "Popup geöffnet",
    overlayClosed: "Popup geschlossen",
    selectCreateOption: (query) => `"${query}" erstellen`,
    wizardNext: "Weiter",
    wizardPrevious: "Zurück",
    wizardFinish: "Fertig",
    wizardStepStatus: (current, total) => `Schritt ${current} von ${total}`,
});
export const MDY_I18N_MESSAGES_FR = Object.freeze({
    searchPlaceholder: "Rechercher…",
    selectPlaceholder: "Sélectionner…",
    entryUnreadable: "Cette saisie n’a pas pu être lue. Corrigez-la, ou videz le champ.",
    noResults: "Aucun résultat",
    colorPresetsHeader: "Préréglages",
    selectColorPrefix: "Sélectionner la couleur",
    colorCustomEntry: "Toutes les couleurs…",
    colorCustomValue: "Personnalisé, {value}",
    colorHexLabel: "Valeur hexadécimale de la couleur",
    timepickerOpenLabel: "Ouvrir le sélecteur d'heure",
    timepickerCancel: "Annuler",
    timepickerConfirm: "OK",
    timepickerHourLabel: "Heure",
    timepickerMinuteLabel: "Minute",
    timepickerSwitchToDial: "Passer à la vue cadran",
    timepickerSwitchToInput: "Passer à la saisie texte",
    datepickerToggleLabel: "Afficher/masquer le calendrier",
    datepickerCancel: "Annuler",
    datepickerConfirm: "OK",
    datepickerSelectFallback: "Sélectionner une date",
    datepickerChooseDate: "Choisir la date",
    datepickerPreviousMonth: "Mois précédent",
    datepickerNextMonth: "Mois suivant",
    datepickerChangeView: (current) => `Changer de vue, actuellement ${current}`,
    daterangeChooseRange: "Choisir la plage de dates",
    daterangeSelectFallback: "Sélectionner la plage",
    daterangeStartLabel: "Date de début",
    daterangeEndLabel: "Date de fin",
    daterangePickStartHint: "Cliquez pour définir la date de début",
    daterangePickEndHint: "Cliquez pour définir la date de fin",
    loading: "Chargement…",
    increase: "Augmenter",
    decrease: "Diminuer",
    searchOptionsLabel: "Rechercher dans les options",
    chipRemoveLabel: "Retirer",
    chipMoveEarlierLabel: "Déplacer avant",
    chipMoveLaterLabel: "Déplacer après",
    quantitySettled: "{value}, {count}",
    quantityAtMinimum: "{value}, {count}, minimum",
    selectionAdded: "{value} ajouté, {count} sélectionnés",
    selectionRemoved: "{value} retiré, {count} sélectionnés",
    selectionEmpty: "Aucune sélection",
    selectionRemovedLast: "{value} supprimé, aucune sélection",
    selectionAddedMany: "{moved} ajoutés, {count} sélectionnés",
    selectionRemovedMany: "{moved} retirés, {count} sélectionnés",
    selectionRemovedManyLast: "{moved} retirés, aucune sélection",
    selectionMoved: "{value}, déplacé en position {position} sur {count}",
    keyGuideOpen: "{keys} l'ouvre",
    keyGuideMove: "{keys} le parcourt",
    keyGuideStep: "{keys} change la valeur",
    keyGuideToggle: "{keys} le bascule",
    keyGuideCommit: "{keys} valide",
    keyGuideCancel: "{keys} le ferme",
    keyGuideRemove: "{keys} en retire un",
    keyGuideGrab: "{keys} en saisit un pour le déplacer",
    keyGuideOr: " ou ",
    keyGuideJoin: ", ",
    keyGuideSpace: "Espace",
    selectionGrabbed: "{value} saisi, {position} sur {count}. Utilisez les flèches pour le déplacer, Entrée pour le poser, Échap pour le remettre",
    selectionDropped: "{value} posé en position {position} sur {count}",
    selectionReturned: "{value} remis en position {position} sur {count}",
    selectionCount: "{count} sélectionnés",
    clearSelection: "Effacer la sélection",
    chipsHiddenShort: "+{count}",
    chipsHidden: "{count} autres non affichés",
    wayBackLabel: "Annuler",
    wayBackRemoved: "{value} retiré",
    wayBackMoved: "{value} déplacé",
    wayBackCleared: "{count} éléments effacés",
    chipDecrementLabel: "Un de moins",
    chipIncrementLabel: "Un de plus",
    fileSelect: "Sélectionner un fichier",
    fileSelectMultiple: "Sélectionner des fichiers",
    fileNoneSelected: "Aucun fichier sélectionné",
    fileClearSelection: "Effacer la sélection",
    fileRejected: (names) => `Non acceptés : ${namesInMessage(names)}`,
    overlayOpened: "Popup ouvert",
    overlayClosed: "Popup fermé",
    selectCreateOption: (query) => `Créer « ${query} »`,
    wizardNext: "Suivant",
    wizardPrevious: "Retour",
    wizardFinish: "Terminer",
    wizardStepStatus: (current, total) => `Étape ${current} sur ${total}`,
});
export const MDY_I18N_MESSAGES_ES = Object.freeze({
    searchPlaceholder: "Buscar…",
    selectPlaceholder: "Seleccionar…",
    entryUnreadable: "No se ha podido leer. Corríjalo, o vacíe el campo.",
    noResults: "Sin resultados",
    colorPresetsHeader: "Preajustes",
    selectColorPrefix: "Seleccionar color",
    colorCustomEntry: "Todos los colores…",
    colorCustomValue: "Personalizado, {value}",
    colorHexLabel: "Valor hexadecimal del color",
    timepickerOpenLabel: "Abrir selector de hora",
    timepickerCancel: "Cancelar",
    timepickerConfirm: "OK",
    timepickerHourLabel: "Hora",
    timepickerMinuteLabel: "Minuto",
    timepickerSwitchToDial: "Cambiar a la vista de esfera",
    timepickerSwitchToInput: "Cambiar a la entrada de texto",
    datepickerToggleLabel: "Mostrar/ocultar calendario",
    datepickerCancel: "Cancelar",
    datepickerConfirm: "OK",
    datepickerSelectFallback: "Seleccionar fecha",
    datepickerChooseDate: "Elegir fecha",
    datepickerPreviousMonth: "Mes anterior",
    datepickerNextMonth: "Mes siguiente",
    datepickerChangeView: (current) => `Cambiar vista, actualmente ${current}`,
    daterangeChooseRange: "Elegir intervalo de fechas",
    daterangeSelectFallback: "Seleccionar intervalo",
    daterangeStartLabel: "Fecha de inicio",
    daterangeEndLabel: "Fecha de fin",
    daterangePickStartHint: "Haz clic para establecer la fecha de inicio",
    daterangePickEndHint: "Haz clic para establecer la fecha de fin",
    loading: "Cargando…",
    increase: "Aumentar",
    decrease: "Disminuir",
    searchOptionsLabel: "Buscar en las opciones",
    chipRemoveLabel: "Quitar",
    chipMoveEarlierLabel: "Mover antes",
    chipMoveLaterLabel: "Mover después",
    quantitySettled: "{value}, {count}",
    quantityAtMinimum: "{value}, {count}, mínimo",
    selectionAdded: "{value} añadido, {count} seleccionados",
    selectionRemoved: "{value} quitado, {count} seleccionados",
    selectionEmpty: "Nada seleccionado",
    selectionRemovedLast: "{value} eliminado, nada seleccionado",
    selectionAddedMany: "{moved} añadidos, {count} seleccionados",
    selectionRemovedMany: "{moved} eliminados, {count} seleccionados",
    selectionRemovedManyLast: "{moved} eliminados, nada seleccionado",
    selectionMoved: "{value}, movido a la posición {position} de {count}",
    keyGuideOpen: "{keys} lo abre",
    keyGuideMove: "{keys} lo recorre",
    keyGuideStep: "{keys} cambia el valor",
    keyGuideToggle: "{keys} lo conmuta",
    keyGuideCommit: "{keys} confirma",
    keyGuideCancel: "{keys} lo cierra",
    keyGuideRemove: "{keys} quita uno",
    keyGuideGrab: "{keys} agarra uno para moverlo",
    keyGuideOr: " o ",
    keyGuideJoin: ", ",
    keyGuideSpace: "Espacio",
    selectionGrabbed: "{value} agarrado, {position} de {count}. Use las flechas para moverlo, Intro para soltarlo, Escape para devolverlo",
    selectionDropped: "{value} soltado en la posición {position} de {count}",
    selectionReturned: "{value} devuelto a la posición {position} de {count}",
    selectionCount: "{count} seleccionados",
    clearSelection: "Vaciar la selección",
    chipsHiddenShort: "+{count}",
    chipsHidden: "{count} más no mostrados",
    wayBackLabel: "Deshacer",
    wayBackRemoved: "{value} eliminado",
    wayBackMoved: "{value} movido",
    wayBackCleared: "{count} elementos eliminados",
    chipDecrementLabel: "Uno menos",
    chipIncrementLabel: "Uno más",
    fileSelect: "Seleccionar archivo",
    fileSelectMultiple: "Seleccionar archivos",
    fileNoneSelected: "Ningún archivo seleccionado",
    fileClearSelection: "Borrar selección",
    fileRejected: (names) => `No aceptados: ${namesInMessage(names)}`,
    overlayOpened: "Ventana emergente abierta",
    overlayClosed: "Ventana emergente cerrada",
    selectCreateOption: (query) => `Crear "${query}"`,
    wizardNext: "Siguiente",
    wizardPrevious: "Atrás",
    wizardFinish: "Finalizar",
    wizardStepStatus: (current, total) => `Paso ${current} de ${total}`,
});
export const MDY_I18N_PRESETS = Object.freeze({
    en: MDY_I18N_MESSAGES_DEFAULT,
    it: MDY_I18N_MESSAGES_IT,
    de: MDY_I18N_MESSAGES_DE,
    fr: MDY_I18N_MESSAGES_FR,
    es: MDY_I18N_MESSAGES_ES,
});
/** Default BCP 47 tag per built-in language preset. */
export const MDY_I18N_DEFAULT_TAGS = Object.freeze({
    en: "en-US",
    it: "it-IT",
    de: "de-DE",
    fr: "fr-FR",
    es: "es-ES",
});
/**
 * The messages a locale tag asks for.
 *
 * A tag is `it-IT`, `it`, or `IT-it`; a preset is keyed by the primary subtag alone, because a
 * region does not change what a confirm button says. An unknown tag answers with the default rather
 * than nothing: a renderer that cannot find a translation shows English, it does not show blanks.
 *
 * Stated once. Three renderers each parsing a tag is three answers to "what does `pt-BR` get".
 */
export function messagesForLocale(tag) {
    const primary = (tag ?? "").split("-")[0]?.toLowerCase() ?? "";
    return MDY_I18N_PRESETS[primary] ?? MDY_I18N_MESSAGES_DEFAULT;
}
